import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import apiUtils from '../../global/utils/api';

import {
  handleCreateFulfilled
  , handleFetchSinglePending
  , handleFetchSingleFulfilled
  , handleFetchSingleFromListFulfilled
  , handleFetchSingleRejected
  , handleFetchListPending
  , handleFetchListFulfilled
  , handleFetchListRejected
  , handleMutationPending
  , handleMutationFulfilled
  , handleMutationRejected
  , handleDeletePending
  , handleDeleteFulfilled
  , handleDeleteRejected
  , shouldFetch
  , INITIAL_STATE
  , handleInvalidateQuery
  , handleInvalidateQueries
  , handleAddSingleToList
  , handleAddManyToList
} from '../../global/utils/storeUtils';


// First define all API calls for tag

// define and export the strings for the different specific tag endpoints once here because the idea of using strings in the component gives me hives.
// we'll catch for these strings on the server side and apply the correct permissions to the query.
// these are passed in to the tagService hooks at the component level as the endpoint argument.
// NOTE: If any of these are functions that require arguments, return null until all arguments are provided so the fetching hook will hold off on the fetch instead of calling an endpoint that will fail.
// export const myExampleEndpoint = 'example-endpoint';
// export const myEndpointWithArgs = (arg1, arg2) => {
//   if(!arg1 || !arg2) return null;
//   return `example-endpoint/${arg1}/${arg2}`;
// };
/**
 * The functions below, called thunks, allow us to perform async logic. They
 * can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
 * will call the thunk with the `dispatch` function as the first argument. Async
 * code can then be executed and other actions can be dispatched. Thunks are
 * typically used to make async requests.
 *
 * In practice we won't dispatch these directly, they will be dispatched by tagService which has a nicer api built on hooks.
 */

// CREATE
export const sendCreateTag = createAsyncThunk(
  'tag/sendCreate'
  , async (newTag) => {
    const endpoint = `/api/tags`;
    const response = await apiUtils.callAPI(endpoint, 'POST', newTag);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// READ
export const fetchSingleTag = createAsyncThunk(
  'tag/fetchSingle'
  , async (id) => {
    const endpoint = `/api/tags/${id}`;
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);
export const fetchTagList = createAsyncThunk(
  'tag/fetchList' // this is the action name that will show up in the console logger.
  , async (listArgs) => {
    const endpoint = `/api/tags${listArgs}`;
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// for each resource we can add as many endpoints as we want in this format and we only need two actions to handle them.
// this will hit the same endpoint as the list version, but the store will handle the returned array and access the single item in it.
export const fetchSingleTagAtEndpoint = createAsyncThunk(
  'tag/fetchSingleWithFilter' // this is the action name that will show up in the console logger.
  , async (query) => {
    const endpoint = `/api/tags${query}` // example: `/api/tags/logged-in?${queryString}`
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);
export const fetchTagListAtEndpoint = createAsyncThunk(
  'tag/fetchListWithFilter' // this is the action name that will show up in the console logger.
  , async (query) => {
    const endpoint = `/api/tags${query}`; // example: `/api/tags/logged-in?${queryString}`
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// UPDATE
export const sendUpdateTag = createAsyncThunk(
  'tag/sendUpdate'
  , async ({ _id, ...updates }) => {
    const endpoint = `/api/tags/${_id}`;
    const response = await apiUtils.callAPI(endpoint, 'PUT', updates);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// DELETE
export const sendDeleteTag = createAsyncThunk(
  'tag/sendDelete'
  , async (id) => {
    const endpoint = `/api/tags/${id}`;
    const response = await apiUtils.callAPI(endpoint, 'DELETE');
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// next define the store's initial state, all of our store utils rely on a specific state shape, so use the constant
const initialState = { ...INITIAL_STATE };

// define the tagSlice. This is a combination of actions and reducers. More info: https://redux-toolkit.js.org/api/createSlice
export const tagSlice = createSlice({
  name: 'tag'
  , initialState
  /**
   * The `reducers` field lets us define reducers and generate associated actions.
   * Unlike the selectors defined at the bottom of this file, reducers only have access
   * to this specific reducer and not the entire store.
   *
   * Again, we will not dispatch these directly, they will be dispatched by tagService.
   */
  , reducers: {
    invalidateQuery: handleInvalidateQuery
    , invalidateQueries: handleInvalidateQueries
    , addTagToList: handleAddSingleToList
    , addTagsToList: handleAddManyToList
  }

  /**
   * The `extraReducers` field lets the slice handle actions defined elsewhere,
   * including actions generated by createAsyncThunk or in other slices.
   * We'll use them to track our server request status.
   *
   * We'll add a case for each API call defined at the top of the file to dictate
   * what happens during each API call lifecycle.
   */
  , extraReducers: (builder) => {
    builder
      // CREATE
      .addCase(sendCreateTag.fulfilled, handleCreateFulfilled)

      // READ
      .addCase(fetchSingleTag.pending, handleFetchSinglePending)
      .addCase(fetchSingleTag.fulfilled, handleFetchSingleFulfilled)
      .addCase(fetchSingleTag.rejected, handleFetchSingleRejected)
      .addCase(fetchTagList.pending, handleFetchListPending)
      // because lists are returned from the server named for their resource, we need to pass a `listKey` so the util can properly handle the response
      .addCase(fetchTagList.fulfilled, (state, action) => handleFetchListFulfilled(state, action, 'tags'))
      .addCase(fetchTagList.rejected, handleFetchListRejected)

      // permission protected single fetches
      .addCase(fetchSingleTagAtEndpoint.pending, handleFetchSinglePending)
      // these endpoints return named lists, we need to pass a `listKey` so the util can properly handle the response
      .addCase(fetchSingleTagAtEndpoint.fulfilled, (state, action) => handleFetchSingleFromListFulfilled(state, action, 'tags'))
      .addCase(fetchSingleTagAtEndpoint.rejected, handleFetchSingleRejected)
      // permission protected list fetches
      .addCase(fetchTagListAtEndpoint.pending, handleFetchListPending)
      .addCase(fetchTagListAtEndpoint.fulfilled, (state, action) => handleFetchListFulfilled(state, action, 'tags'))
      .addCase(fetchTagListAtEndpoint.rejected, handleFetchListRejected)

      // UPDATE
      .addCase(sendUpdateTag.pending, handleMutationPending)
      .addCase(sendUpdateTag.fulfilled, handleMutationFulfilled)
      .addCase(sendUpdateTag.rejected, handleMutationRejected)

      // DELETE
      .addCase(sendDeleteTag.pending, handleDeletePending)
      .addCase(sendDeleteTag.fulfilled, handleDeleteFulfilled)
      .addCase(sendDeleteTag.rejected, handleDeleteRejected)
  }
});

// export the actions for the reducers defined above
export const { invalidateQuery, invalidateQueries, addTagToList, addTagsToList } = tagSlice.actions;


// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.
// accepts an optional listFetch action so we can use it for other list fetches, defaults to fetchTagList
export const fetchListIfNeeded = (queryKey, listFetch = fetchTagList) => (dispatch, getState) => {
  const tagQuery = getState().tag.listQueries[queryKey];
  if(shouldFetch(tagQuery)) {
    dispatch(listFetch(queryKey));
  } else {
    // console.log('No need to fetch, fresh query in cache');
  }
};

// accepts an optional singleFetch action so we can use it for other single fetches, defaults to fetchSingleTag
export const fetchSingleIfNeeded = (id, singleFetch = fetchSingleTag) => (dispatch, getState) => {
  const tagQuery = getState().tag.singleQueries[id];
  if(shouldFetch(tagQuery)) {
    dispatch(singleFetch(id));
  } else {
    // console.log('No need to fetch, fresh query in cache');
  }
}

export default tagSlice.reducer;
